import type { RowPredicateLite } from '../../../core/bridge/connect-client';

/*
 * Saved filters for the table screen — the assignment's "a saved view: a filtered
 * table, kept, named, and openable again", and Metabase's idea of turning a filter
 * into a named thing without a query language.
 *
 * The filter machinery already existed and already pushes down to the source; all
 * that was missing was keeping one. This module is the pure half — naming,
 * replacing, ordering, bounding, and the storage round-trip — so the rules are
 * testable without a DOM.
 *
 * SCOPE, stated plainly: these live in localStorage, per browser. That matches the
 * selection memory the endpoint viewport already keeps, and it is the honest limit
 * of a contained change — a saved view you would find on another machine, or share
 * with a colleague, needs server-side per-user storage (the `user_data` KV the
 * symbol grid's labels use) and is a bigger decision than this.
 *
 * Saved AGAINST A TABLE, not just an integration: a predicate names columns, and
 * the same filter applied to a different table would reference columns that do not
 * exist there — the source would refuse it in prose, which is correct but a poor
 * way to find out.
 */

export interface SavedFilter {
  name: string;
  /** The table these predicates were built against — see the note above. */
  table: string;
  predicates: RowPredicateLite[];
}

/** Bound on how many a single integration may keep. Generous for real use and
 *  small enough that a runaway loop cannot fill a browser's storage quota. */
export const MAX_SAVED = 50;

export function storeKey(integrationId: string): string {
  return `vitesse:intg:${integrationId}:filters`;
}

/** Add or replace by name (per table), newest first so the most recent work is
 *  nearest to hand. Replacing keeps the entry's POSITION rather than moving it,
 *  so re-saving a filter you are iterating on does not shuffle the list under you.
 *
 *  Returns a new array; never mutates the input. */
export function upsertFilter(saved: readonly SavedFilter[], entry: SavedFilter): SavedFilter[] {
  const name = entry.name.trim();
  const next: SavedFilter = { ...entry, name, predicates: [...entry.predicates] };
  const at = saved.findIndex((s) => s.name === name && s.table === entry.table);
  if (at >= 0) {
    const copy = saved.slice();
    copy[at] = next;
    return copy;
  }
  return [next, ...saved].slice(0, MAX_SAVED);
}

export function removeFilter(saved: readonly SavedFilter[], name: string, table: string): SavedFilter[] {
  return saved.filter((s) => !(s.name === name && s.table === table));
}

/** Those saved against one table — what the bar offers while you are looking at it. */
export function filtersFor(saved: readonly SavedFilter[], table: string): SavedFilter[] {
  return saved.filter((s) => s.table === table);
}

/** Whether a name may be saved: non-empty after trimming, and bounded so a pasted
 *  essay cannot become a chip. Duplicate names are allowed — they REPLACE, which
 *  is the behaviour a person expects from "save" on something they just edited. */
export function validName(name: string): boolean {
  const n = name.trim();
  return n.length > 0 && n.length <= 60;
}

/** Read the store, tolerating anything that is not what we wrote. A corrupt or
 *  hand-edited entry means "no saved filters", never a crashed screen: this runs
 *  on the render path of a page whose actual job is showing a customer's data. */
export function loadFilters(raw: string | null): SavedFilter[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: SavedFilter[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== 'object') continue;
      const { name, table, predicates } = item as Partial<SavedFilter>;
      if (typeof name !== 'string' || typeof table !== 'string' || !Array.isArray(predicates)) continue;
      if (!validName(name)) continue;
      // A predicate with no column cannot be sent, and one with an unknown op
      // would be refused by the source; drop the entry rather than save a filter
      // that fails the moment it is applied.
      const preds = predicates.filter(
        (p): p is RowPredicateLite => !!p && typeof p === 'object' && typeof (p as RowPredicateLite).column === 'string' && !!(p as RowPredicateLite).op,
      );
      if (preds.length === 0) continue;
      out.push({ name, table, predicates: preds });
    }
    return out.slice(0, MAX_SAVED);
  } catch {
    return [];
  }
}

export function dumpFilters(saved: readonly SavedFilter[]): string {
  return JSON.stringify(saved.slice(0, MAX_SAVED));
}
